import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText, CheckCircle, Clock, CreditCard, Send, Archive, Loader2, Cog, DollarSign, Ban, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import DashboardLayout from '../components/DashboardLayout';
import { useLiveSyncPolling } from '../hooks/useLiveSyncPolling';
import { LiveSyncIndicator } from '../components/service-requests/LiveSyncIndicator';
import { RequestDetailsView } from '../components/service-requests/RequestDetailsView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// أنواع الخدمات
const SERVICE_TYPES = {
  'visa-id': { nameAr: 'طلبات التأشيرة والهوية', nameEn: 'Visa & Emirates ID', color: '#06b6d4' },
  'tourist-visa': { nameAr: 'التأشيرات السياحية لجميع الدول', nameEn: 'Tourist Visas — All Countries', color: '#0ea5e9' },
  'doc-certification': { nameAr: 'تصديق المستندات', nameEn: 'Document Certification', color: '#f59e0b' },
  'driving-license': { nameAr: 'خدمات رخصة القيادة', nameEn: 'Driving License Services', color: '#10b981' },
  'printing-docs-general': { nameAr: 'الطلبات العامة — الطباعة وتخليص المستندات', nameEn: 'General Printing & Documents', color: '#a1a1aa' },
  'company-formation': { nameAr: 'تأسيس الشركات', nameEn: 'Company Formation', color: '#10b981' },
  'printing-docs': { nameAr: 'الطباعة وتخليص المستندات', nameEn: 'Printing & Documents', color: '#f59e0b' },
  'legal-translation': { nameAr: 'خدمات الترجمة القانونية', nameEn: 'Legal Translation', color: '#3b82f6' },
  'bill-payment': { nameAr: 'خدمات دفع الفواتير', nameEn: 'Bill Payment', color: '#ec4899' },
  'tax-agent': { nameAr: 'خدمات الوكيل الضريبي', nameEn: 'Tax Agent', color: '#8b5cf6' },
};

// الأنواع الفرعية داخل "الطباعة وتخليص المستندات" — تعود لصفحة الهَب الخاصة بها
const PRINTING_SUBTYPES = ['visa-id', 'tourist-visa', 'doc-certification', 'driving-license', 'printing-docs-general'];

// حالات الطلبات — same flow across ALL service types (Feb 2026).
// Order matters: it's the customer-facing progression from submission → final delivery.
const REQUEST_STATUSES = [
  { id: 'submitted',        nameAr: 'تم الإرسال',           nameEn: 'Submitted',        icon: Send,        color: '#6b7280' },
  { id: 'accepted',         nameAr: 'تم قبول الطلب',        nameEn: 'Accepted',         icon: CheckCircle, color: '#10b981' },
  { id: 'processing',       nameAr: 'جاري معالجة الطلب',    nameEn: 'Processing',       icon: Clock,       color: '#f59e0b' },
  { id: 'pending_payment',  nameAr: 'مطلوب دفع',            nameEn: 'Payment Required', icon: CreditCard,  color: '#ef4444' },
  // NEW (added Feb 2026): explicit stage between "paid" and "delivered" where
  // the ops team finalises the deliverable before sending it back to the customer.
  { id: 'final_processing', nameAr: 'المعالجة النهائية',    nameEn: 'Final Processing', icon: Cog,         color: '#a855f7' },
  { id: 'completed',        nameAr: 'تم الانتهاء والإرسال', nameEn: 'Completed & Sent', icon: Archive,     color: '#3b82f6' },
];

export default function ServiceRequests() {
  const navigate = useNavigate();
  const { serviceType } = useParams();
  const { token } = useAuth();
  const { language } = useLanguage();
  
  const [activeView, setActiveView] = useState('list'); // list, details
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Payment dialog (used when transitioning INTO pending_payment)
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [priceForm, setPriceForm] = useState({ price: '', currency: 'AED', payment_notes: '' });

  // Cancel confirmation
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelNote, setCancelNote] = useState('');

  const headers = { Authorization: `Bearer ${token}` };
  const serviceInfo = SERVICE_TYPES[serviceType] || { nameAr: 'خدمة', nameEn: 'Service', color: '#c9a227' };
  const isPrintingSub = PRINTING_SUBTYPES.includes(serviceType);
  // legacy requests are stored under 'printing-docs' — the "general" card reads them
  const queryType = serviceType === 'printing-docs-general' ? 'printing-docs' : serviceType;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeView]);

  useEffect(() => {
    fetchRequests();
  }, [statusFilter, serviceType]);

  // Live sync: polls the backend every 8s to catch payment confirmations
  // pushed by the customer platform. Keeps the details drawer in sync too.
  const { active: liveSyncActive, lastSyncAt, toggle: toggleLiveSync } = useLiveSyncPolling({
    enabled: true,
    onPoll: () => fetchRequests({ silent: true }),
    onRefreshSelected:
      activeView === 'details' && selectedRequest?.id
        ? () => refreshSelectedRequest(selectedRequest.id)
        : null,
    deps: [statusFilter, serviceType, activeView, selectedRequest?.id],
  });

  const refreshSelectedRequest = async (requestId) => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/service-requests/${requestId}`,
        { headers },
      );
      const fresh = res.data?.data;
      if (fresh && selectedRequest) {
        // Detect payment confirmation transition and celebrate.
        if (
          selectedRequest.status === 'pending_payment' &&
          fresh.status === 'final_processing'
        ) {
          toast.success(
            language === 'ar'
              ? `تم استلام دفعة العميل — تقدّم الطلب إلى "المعالجة النهائية"`
              : `Customer payment received — request advanced to "Final Processing"`,
          );
        }
        setSelectedRequest(fresh);
      }
    } catch {
      /* silent */
    }
  };

  const fetchRequests = async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      let url = `${BACKEND_URL}/api/admin/service-requests?service_type=${queryType}`;
      if (statusFilter !== 'all') {
        url += `&status_filter=${statusFilter}`;
      }
      const res = await axios.get(url, { headers });

      // Detect any request that transitioned pending_payment → final_processing
      // during a silent poll so the admin sees a live toast even from the list.
      if (silent && requests.length > 0) {
        const oldById = Object.fromEntries(requests.map((r) => [r.id, r.status]));
        (res.data?.data || []).forEach((r) => {
          if (
            oldById[r.id] === 'pending_payment' &&
            r.status === 'final_processing'
          ) {
            toast.success(
              language === 'ar'
                ? `💰 تم استلام الدفع للطلب #${r.id.substring(0, 6)} — انتقل تلقائياً إلى "المعالجة النهائية"`
                : `💰 Payment received on request #${r.id.substring(0, 6)} — auto-advanced to "Final Processing"`,
              { duration: 6000 },
            );
          }
        });
      }

      setRequests(res.data.data || []);
    } catch (err) {
      if (!silent) toast.error('فشل في جلب الطلبات');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const updateRequestStatus = async (requestId, newStatus, extraPayload = {}) => {
    setUpdatingStatus(true);
    try {
      const res = await axios.put(
        `${BACKEND_URL}/api/admin/service-requests/${requestId}/status`,
        { status: newStatus, ...extraPayload },
        { headers },
      );
      toast.success(language === 'ar' ? `تم التحديث → ${getStatusInfo(newStatus).nameAr}` : `Updated → ${getStatusInfo(newStatus).nameEn}`);
      fetchRequests();
      if (selectedRequest && res.data?.data) {
        setSelectedRequest(res.data.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في تحديث الحالة' : 'Failed to update status'));
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Handle click on "next step" — special case for pending_payment (opens price dialog).
  const handleAdvance = (request) => {
    const next = request.next_status || getNextStatus(request.status);
    if (!next) return;
    if (next === 'pending_payment') {
      setPriceForm({ price: request.price || '', currency: request.currency || 'AED', payment_notes: '' });
      setPriceDialogOpen(true);
      return;
    }
    updateRequestStatus(request.id, next);
  };

  const submitPrice = () => {
    if (!priceForm.price || parseFloat(priceForm.price) <= 0) {
      toast.error(language === 'ar' ? 'أدخل السعر' : 'Enter a valid price');
      return;
    }
    updateRequestStatus(selectedRequest.id, 'pending_payment', {
      price: parseFloat(priceForm.price),
      currency: priceForm.currency,
      payment_notes: priceForm.payment_notes,
    });
    setPriceDialogOpen(false);
  };

  const submitCancel = () => {
    updateRequestStatus(selectedRequest.id, 'cancelled', { note: cancelNote });
    setCancelConfirmOpen(false);
    setCancelNote('');
  };

  const getNextStatus = (current) => {
    const idx = REQUEST_STATUSES.findIndex((s) => s.id === current);
    if (idx < 0 || idx >= REQUEST_STATUSES.length - 1) return null;
    return REQUEST_STATUSES[idx + 1].id;
  };

  const getStatusInfo = (status) => {
    return REQUEST_STATUSES.find(s => s.id === status) || REQUEST_STATUSES[0];
  };

  const getBreadcrumb = () => {
    const crumbs = [
      { labelAr: 'CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC', path: '/manage/cloudgm' },
      { labelAr: 'منصة العملاء (الإشراف)', labelEn: 'Customer Platform (Supervision)', path: '/manage/cloudgm' },
      { labelAr: 'طلبات الخدمات', labelEn: 'Service Requests', path: '/manage/cloudgm?tab=services-categories' },
    ];
    if (isPrintingSub) {
      crumbs.push({
        labelAr: 'الطباعة وتخليص المستندات',
        labelEn: 'Printing & Documents',
        path: '/control/cloudgm/service-requests/printing-docs',
      });
    }
    crumbs.push({
      labelAr: serviceInfo.nameAr,
      labelEn: serviceInfo.nameEn,
      path: activeView === 'details' ? `/control/cloudgm/service-requests/${serviceType}` : null,
    });
    if (activeView === 'details') {
      crumbs.push({ labelAr: 'تفاصيل الطلب', labelEn: 'Request Details', path: null });
    }
    return crumbs;
  };

  const getBackPath = () => {
    if (activeView === 'details') {
      return null; // Custom back handling (details → list)
    }
    return isPrintingSub
      ? '/control/cloudgm/service-requests/printing-docs'
      : '/manage/cloudgm?tab=services-categories';
  };

  const handleBack = () => {
    if (activeView === 'details') {
      setActiveView('list');
      setSelectedRequest(null);
    }
  };

  const getStatusCounts = () => {
    const counts = { all: requests.length };
    REQUEST_STATUSES.forEach(s => {
      counts[s.id] = requests.filter(r => r.status === s.id).length;
    });
    return counts;
  };

  const filteredRequests = statusFilter === 'all' 
    ? requests 
    : requests.filter(r => r.status === statusFilter);

  return (
    <DashboardLayout
      title={language === 'ar' ? `طلبات ${serviceInfo.nameAr}` : `${serviceInfo.nameEn} Requests`}
      titleEn={`${serviceInfo.nameEn} Requests`}
      subtitle={language === 'ar' ? 'إدارة الطلبات الواردة' : 'Manage incoming requests'}
      subtitleEn="Manage incoming requests"
      screenCode="SRV-002"
      backPath={getBackPath()}
      onBackClick={activeView === 'details' ? handleBack : undefined}
      breadcrumb={getBreadcrumb()}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Live-sync indicator (always visible above list & details) */}
        <LiveSyncIndicator
          active={liveSyncActive}
          lastSyncAt={lastSyncAt}
          loading={loading}
          language={language}
          onToggle={toggleLiveSync}
          onRefresh={() => fetchRequests()}
        />

        {/* List View */}
        {activeView === 'list' && (
          <>
            {/* Status Filters */}
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  statusFilter === 'all'
                    ? 'bg-[#c9a227] text-black'
                    : 'bg-[#1a1a2e] text-white border border-[#2a2a4e] hover:border-[#c9a227]'
                }`}
                data-testid="filter-all"
              >
                {language === 'ar' ? 'الكل' : 'All'} ({getStatusCounts().all})
              </button>
              {REQUEST_STATUSES.map(status => {
                const StatusIcon = status.icon;
                return (
                  <button
                    key={status.id}
                    onClick={() => setStatusFilter(status.id)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                      statusFilter === status.id
                        ? 'text-black'
                        : 'bg-white/[0.02] backdrop-blur-xl text-white border border-white/10 hover:border-[#c9a227]/50'
                    }`}
                    style={statusFilter === status.id ? { backgroundColor: status.color } : {}}
                    data-testid={`filter-${status.id}`}
                  >
                    <StatusIcon className="h-4 w-4" />
                    {language === 'ar' ? status.nameAr : status.nameEn}
                    {getStatusCounts()[status.id] > 0 && (
                      <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                        {getStatusCounts()[status.id]}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Requests List */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-[#c9a227]" />
              </div>
            ) : filteredRequests.length === 0 ? (
              <div className="text-center py-16 text-[#808080] bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10">
                <FileText className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">{language === 'ar' ? 'لا توجد طلبات' : 'No requests'}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredRequests.map(request => {
                  const statusInfo = getStatusInfo(request.status);
                  const StatusIcon = statusInfo.icon;
                  return (
                    <div
                      key={request.id}
                      onClick={() => {
                        setSelectedRequest(request);
                        setActiveView('details');
                        // fetch FULL details (list payload may carry trimmed attachments)
                        axios.get(`${BACKEND_URL}/api/admin/service-requests/${request.id}`, { headers })
                          .then((res) => { if (res.data?.data) setSelectedRequest(res.data.data); })
                          .catch(() => {});
                      }}
                      className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-[#c9a227]/50 transition-all cursor-pointer group hover:bg-white/[0.04]"
                      data-testid={`request-${request.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div 
                            className="w-12 h-12 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: `${statusInfo.color}20` }}
                          >
                            <StatusIcon className="h-6 w-6" style={{ color: statusInfo.color }} />
                          </div>
                          <div>
                            <h3 className="font-bold text-white">
                              {language === 'ar' ? 'طلب' : 'Request'} #{request.id?.substring(0, 6)}
                            </h3>
                            <p className="text-sm text-[#808080]">{request.customer_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span 
                            className="px-3 py-1.5 rounded-lg text-sm font-medium"
                            style={{ backgroundColor: `${statusInfo.color}20`, color: statusInfo.color }}
                          >
                            {language === 'ar' ? statusInfo.nameAr : statusInfo.nameEn}
                          </span>
                          <p className="text-xs text-[#606060]">
                            {new Date(request.created_at).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-AE')}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Details View */}
        {activeView === 'details' && selectedRequest && (
          <RequestDetailsView
            request={selectedRequest}
            language={language}
            updatingStatus={updatingStatus}
            statuses={REQUEST_STATUSES}
            getNextStatus={getNextStatus}
            onAdvance={handleAdvance}
            onCancelClick={() => setCancelConfirmOpen(true)}
          />
        )}
      </section>

      {/* Price Dialog — for pending_payment transition */}
      <Dialog open={priceDialogOpen} onOpenChange={setPriceDialogOpen}>
        <DialogContent className="bg-[#09090b] border-white/10 text-white" dir={language === 'ar' ? 'rtl' : 'ltr'} data-testid="sr-price-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-[#d4af37]" />
              {language === 'ar' ? 'إرسال طلب الدفع للعميل' : 'Send Payment Request'}
            </DialogTitle>
            <DialogDescription className="text-white/60 text-xs">
              {language === 'ar'
                ? 'حدد السعر ثم أرسله للعميل. سيتحول الطلب تلقائياً إلى "المعالجة النهائية" بمجرد دفع العميل.'
                : 'Set the amount and send it to the customer. The request will auto-advance to "Final Processing" once payment is confirmed.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Label className="text-xs text-white/70">{language === 'ar' ? 'المبلغ *' : 'Amount *'}</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={priceForm.price}
                  onChange={(e) => setPriceForm((p) => ({ ...p, price: e.target.value }))}
                  className="bg-black/40 border-white/10 text-white text-lg font-mono mt-1"
                  placeholder="0.00"
                  data-testid="sr-price-input"
                />
              </div>
              <div>
                <Label className="text-xs text-white/70">{language === 'ar' ? 'العملة' : 'Currency'}</Label>
                <select
                  value={priceForm.currency}
                  onChange={(e) => setPriceForm((p) => ({ ...p, currency: e.target.value }))}
                  className="mt-1 w-full h-10 rounded-md bg-black/40 border border-white/10 text-white text-sm px-3 font-mono"
                  data-testid="sr-price-currency"
                >
                  <option value="AED">AED</option>
                  <option value="USD">USD</option>
                  <option value="SAR">SAR</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-white/70">{language === 'ar' ? 'ملاحظات (اختياري)' : 'Notes (optional)'}</Label>
              <Textarea
                value={priceForm.payment_notes}
                onChange={(e) => setPriceForm((p) => ({ ...p, payment_notes: e.target.value }))}
                rows={2}
                placeholder={language === 'ar' ? 'مثال: يشمل رسوم إصدار + توصيل' : 'e.g. includes issuance + delivery fees'}
                className="bg-black/40 border-white/10 text-white text-sm mt-1"
                data-testid="sr-price-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPriceDialogOpen(false)} className="text-white/70" data-testid="sr-price-cancel">
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={submitPrice} disabled={updatingStatus} className="bg-gradient-to-r from-[#d4af37] to-[#9b7d20] text-black font-bold" data-testid="sr-price-submit">
              {updatingStatus ? <Loader2 className="h-4 w-4 animate-spin me-1.5" /> : <Send className="h-4 w-4 me-1.5" />}
              {language === 'ar' ? 'إرسال للعميل' : 'Send to Customer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel confirmation */}
      <AlertDialog open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
        <AlertDialogContent className="bg-[#09090b] border-white/10 text-white" dir={language === 'ar' ? 'rtl' : 'ltr'}>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-300">
              <AlertTriangle className="h-5 w-5" />
              {language === 'ar' ? 'تأكيد إلغاء الطلب' : 'Confirm Cancellation'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/70 text-sm">
              {language === 'ar'
                ? 'هذا الإجراء نهائي — سيتوقف مسار الطلب ولن يتم استكماله. أدخل سبب الإلغاء أدناه (اختياري):'
                : 'This action is final — the request will stop and not be completed. Optionally add a reason below:'}
            </AlertDialogDescription>
            <Textarea
              value={cancelNote}
              onChange={(e) => setCancelNote(e.target.value)}
              rows={2}
              placeholder={language === 'ar' ? 'سبب الإلغاء...' : 'Reason...'}
              className="bg-black/40 border-white/10 text-white text-sm mt-2"
              data-testid="sr-cancel-note"
            />
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10" data-testid="sr-cancel-abort">
              {language === 'ar' ? 'رجوع' : 'Back'}
            </AlertDialogCancel>
            <AlertDialogAction onClick={submitCancel} className="bg-red-500 hover:bg-red-600 text-white" data-testid="sr-cancel-confirm">
              <Ban className="h-4 w-4 me-1.5" />
              {language === 'ar' ? 'تأكيد الإلغاء' : 'Confirm Cancel'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
